import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { useLSSearch } from "./LSSearch";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAccount } from "@tlc/ls2pac/components/Account";
import { FormTextField } from "@tlc/ls2pac/components/FormTextField";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import {
  SavedSearchesService,
  SaveSearchParams,
} from "../../account/searches/SavedSearches";
import { useToast } from "../../../../contexts/ToastContext";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import {
  isAdvancedSearchTerm,
  isReadingLevelSearch,
} from "../advanced/AdvancedSearchTerm";

interface SaveSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

interface SaveSearchFormInput {
  name: string;
  isConfig: boolean;
}

const SaveSearchDialog = ({ open, onClose }: SaveSearchDialogProps) => {
  const {
    searchParams: { rawTerm, term, facetFilters, branchFilters, sort },
  } = useLSSearch();
  const { showToast } = useToast();
  const { isConfigAdmin } = useAccount();
  const { translate } = useLanguage();

  const defaults = useMemo(() => {
    return {
      name:
        isAdvancedSearchTerm(rawTerm) || isReadingLevelSearch(rawTerm)
          ? ""
          : term,
      isConfig: isConfigAdmin,
    };
  }, [rawTerm, term, isConfigAdmin]);

  const formMethods = useForm<SaveSearchFormInput>({
    defaultValues: defaults,
    mode: "onBlur",
  });
  const { handleSubmit, reset, control } = formMethods;

  // reset on open
  useEffect(() => {
    if (open) {
      reset(defaults);
    }
  }, [open, reset, defaults]);

  const onSubmit = async ({ isConfig, name }: SaveSearchFormInput) => {
    const readingLevelFilters = isReadingLevelSearch(rawTerm)
      ? rawTerm.readingLevelFilters
      : null;
    const payload: SaveSearchParams = {
      expression: term,
      facetFilters,
      branchFilters,
      readingLevelFilters,
      name,
      sortType: sort,
    };
    try {
      await SavedSearchesService.saveSearch(payload, isConfig);
      showToast(translate("savedSearchSuccessMessage"), "success");
      reset();
      onClose();
    } catch (e: unknown) {
      showToast(translate("savedSearchErrorMessage"), "error");
      console.error(e);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isMobile = useIsMobile();

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={isMobile}>
      <FormProvider {...formMethods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{translate("saveSearch")}</DialogTitle>
          <DialogContent
            sx={{
              width: isMobile ? "100%" : "336px",
            }}
          >
            <Stack gap={1} pt={2}>
              <FormTextField<SaveSearchFormInput>
                name={"name"}
                label={translate("searchName")}
                maxLength={255}
                required
              ></FormTextField>
              {isConfigAdmin && (
                <Controller<SaveSearchFormInput, "isConfig">
                  name={"isConfig"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(_, checked) => onChange(checked)}
                        />
                      }
                      label={translate("saveAsConfig")}
                    />
                  )}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={handleClose}>
              {translate("cancel")}
            </Button>
            <Button variant="contained" type="submit">
              {translate("save")}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

interface SaveSearchDialogContextType {
  showDialog: () => void;
}

const SaveSearchDialogContext = createContext<
  SaveSearchDialogContextType | undefined
>(undefined);

export const useSaveSearchDialog = (): SaveSearchDialogContextType => {
  const context = useContext(SaveSearchDialogContext);
  if (!context)
    throw new Error(
      "useSaveSearchDialog must be used within a SaveSearchDialogProvider"
    );
  return context;
};

export const SaveSearchDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(false);

  //   TODO return a Promise with the result of the dialog?
  const showDialog = useCallback(() => {
    // TODO replace promptIfNotLoggedIn this with a context/provider
    LoginClientManager.promptIfNotLoggedIn(() => {
      setOpen(true);
    });
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <SaveSearchDialogContext.Provider value={{ showDialog }}>
      <SaveSearchDialog open={open} onClose={handleClose}></SaveSearchDialog>
      {children}
    </SaveSearchDialogContext.Provider>
  );
};
